// Modules
import cn from 'classnames';

// Types
import type { TPaginationProps } from './Pagination.type';

// Styles
import styles from './styles.module.scss';

// Constants
import { PAGINATION_DEFAULT_PROPS } from './Pagination.constant';

// Components
import PaginationButton from './PaginationButton';
import Icon from '../Icon';

// Helpers
import { getPageNumbers } from './Pagination.helper';
import { kebabCase } from '../../helpers/typography';
import { isNumber } from '../../helpers/typeChecker';

const Pagination = (props: TPaginationProps) => {
	const {
		activePage = PAGINATION_DEFAULT_PROPS.activePage,
		buttonClassName,
		className,
		onClick,
		totalPage = PAGINATION_DEFAULT_PROPS.totalPage,
		...restProps
	} = props;
	const validActivePage = Math.min(Math.max(activePage, 1), totalPage);
	const validTotalPage = Math.max(totalPage, 1);

	const paginationCN = cn(styles.legion_pagination, className);
	const buttonCN = cn(styles.pagination_page, buttonClassName);

	const handleClickPrev = () => {
		if (validActivePage > 1) {
			onClick?.(validActivePage - 1);
		}
	};

	const handleClickPage = (page: number) => () => {
		onClick?.(page);
	};

	const handleClickNext = () => {
		if (validActivePage < validTotalPage) {
			onClick?.(validActivePage + 1);
		}
	};

	const pageNumbers = getPageNumbers(validTotalPage, validActivePage);

	return (
		<div className={paginationCN} {...restProps}>
			<PaginationButton
				className={styles.pagination_prev}
				label={<Icon icon={'chevron-left'}/>}
				onClick={handleClickPrev}
				disabled={validActivePage === 1}
			/>
			{pageNumbers.map((page, index) => {
				const key = kebabCase(`${index}-${page}`);
				if (page === 'dots') {
					return (
						<Icon
							key={key}
							icon={'ellipsis'}
							className={styles.pagination_dots}
							color={'tertiary'}
						/>
					);
				} else {
					const pageNumber = isNumber(page) ? page : parseInt(page, 10)
					return (
						<PaginationButton
							key={key}
							className={buttonCN}
							active={page === validActivePage}
							label={page}
							onClick={handleClickPage(pageNumber)}
						/>
					);
				}
			})}
			<PaginationButton
				className={styles.pagination_next}
				label={<Icon icon={'chevron-right'}/>}
				onClick={handleClickNext}
				disabled={validActivePage === validTotalPage}
			/>
		</div>
	);
}

export default Pagination;
